import { z } from 'zod';
import { DEFAULT_PAGE_SIZE } from '../constants/system/general';
import { FilterOperatorSchema } from './filter';

/**
 * Saved-view payload — the at-rest (JSONB) shape of a saved table view.
 *
 * Design notes:
 * - **JSON-safe only.** A `@db.JsonB` column cannot hold JS `bigint`/`Date`, so
 *   stored filter values are encoded (bigint → numeric string, Date → ISO
 *   string). Each filter records a `valueType` so it can be decoded back to its
 *   exact runtime type by the client, independent of the live field config — a
 *   field's `type` (e.g. `multiselect`) does not say whether its values are
 *   `bigint` (owner ids) or `string` (cause of loss), and a generic Zod union
 *   cannot decode reliably (`z.string()` would win before `z.coerce.bigint()`).
 * - **Resilient.** Each section has a `.catch(default)` so a malformed section
 *   degrades to a safe default rather than throwing; `filters` drops individual
 *   malformed entries (not the whole list). Total garbage (non-object) is handled
 *   by `safeParse` in `reconcileView`.
 * - **`orderBy` is a bounded sort-descriptor array, not a Prisma orderBy object.**
 *   `field` is the column's sortable field path (e.g. `Owner.firstName`); the
 *   client converts to/from the live Prisma shape via `buildPrismaOrderBy` /
 *   `getCurrentSortDirection`.
 */
export const SAVED_VIEW_PAYLOAD_VERSION = 1 as const;

/** Discriminator describing how to decode a stored (JSON-safe) filter value. */
export const StoredValueTypeSchema = z.enum([
    'string',
    'number',
    'boolean',
    'bigint',
    'date',
    'null',
    'string[]',
    'number[]',
    'bigint[]',
    'date[]',
]);
export type StoredValueType = z.infer<typeof StoredValueTypeSchema>;

/**
 * Bounds for at-rest saved-view payloads. These cap the JSONB footprint and the
 * reconciliation work a single view can impose, since a direct tRPC caller could
 * otherwise submit an arbitrarily large (but otherwise valid) payload —
 * `MAX_VIEWS_PER_SCOPE` only limits the row count, not per-row size.
 */
const MAX_STORED_FILTERS = 25;
const MAX_FILTER_VALUE_LENGTH = 1024;
const MAX_FILTER_VALUE_ARRAY_LENGTH = 200;
const MAX_VIEW_PARAMS = 10;
const MAX_VIEW_PARAM_KEY_LENGTH = 64;
const MAX_VIEW_PARAM_STRING_LENGTH = 256;

const storedFilterValueSchema = z.union([
    z.string().max(MAX_FILTER_VALUE_LENGTH),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.string().max(MAX_FILTER_VALUE_LENGTH).nullable()).max(MAX_FILTER_VALUE_ARRAY_LENGTH),
    z.array(z.number().nullable()).max(MAX_FILTER_VALUE_ARRAY_LENGTH),
]);
export type StoredFilterValue = z.infer<typeof storedFilterValueSchema>;

const storedFilterSchema = z.object({
    id: z.string(),
    filterId: z.string().max(128),
    operator: FilterOperatorSchema,
    value: storedFilterValueSchema,
    /** How to decode `value`; optional for forward-compat (falls back to field type). */
    valueType: StoredValueTypeSchema.optional(),
});

/** Per-element schema, exported so the client can validate filters individually. */
export const StoredViewFilterSchema = storedFilterSchema;

/**
 * Per-element-resilient filter array: drops only the malformed entries and keeps
 * the valid ones (a plain `z.array(...).catch([])` would discard the whole list
 * if a single element were bad), then caps the count at `MAX_STORED_FILTERS` so a
 * direct tRPC caller cannot persist an unbounded list.
 */
const resilientStoredFilters = z
    .array(z.unknown())
    .transform((arr) =>
        arr
            .flatMap((el) => {
                const parsed = storedFilterSchema.safeParse(el);
                return parsed.success ? [parsed.data] : [];
            })
            .slice(0, MAX_STORED_FILTERS),
    )
    .catch([]);

const storedSortDescriptorSchema = z.object({
    field: z.string().max(128),
    direction: z.enum(['asc', 'desc']),
});

const storedColumnsSchema = z.object({
    visible: z.array(z.string().max(128)).max(300),
    order: z.array(z.string().max(128)).max(300),
});

/**
 * Non-filter query params a view carries (e.g. the obligation register's `latestPerFamily`).
 * A bounded, page-agnostic bag of JSON-safe scalars: the *page* declares which keys it understands
 * via its `ViewParamsSpec` (client engine) and heals unknown/invalid values to its defaults at
 * apply time — mirroring how filters are interpreted through the page's `FilterConfig`. The list
 * procedure's input schema remains the authoritative boundary for whatever a stored payload claims.
 */
const storedViewParamsSchema = z
    .record(
        z.string().max(MAX_VIEW_PARAM_KEY_LENGTH),
        z.union([z.string().max(MAX_VIEW_PARAM_STRING_LENGTH), z.number(), z.boolean()]),
    )
    .refine((params) => Object.keys(params).length <= MAX_VIEW_PARAMS)
    .catch({});
export type StoredViewParams = z.infer<typeof storedViewParamsSchema>;

export const StoredSavedViewPayloadSchema = z.object({
    schemaVersion: z.literal(SAVED_VIEW_PAYLOAD_VERSION).catch(SAVED_VIEW_PAYLOAD_VERSION),
    filters: resilientStoredFilters,
    orderBy: z.array(storedSortDescriptorSchema).max(4).catch([]),
    columns: storedColumnsSchema.catch({ visible: [], order: [] }),
    pageSize: z.number().int().min(1).max(200).catch(DEFAULT_PAGE_SIZE),
    // Additive to v1: legacy payloads without the key parse to {} via .catch, so no version bump.
    params: storedViewParamsSchema,
});

export type StoredSavedViewPayload = z.infer<typeof StoredSavedViewPayloadSchema>;
export type StoredViewFilter = z.infer<typeof storedFilterSchema>;
export type StoredSortDescriptor = z.infer<typeof storedSortDescriptorSchema>;
export type StoredViewColumns = z.infer<typeof storedColumnsSchema>;

/**
 * Curated presentation vocab for a saved view's chip — a small, bounded set so the picker stays
 * tidy, stored values are validated at the API boundary, and the client can map each token to a
 * design-token colour / lucide icon. Stored as short string tokens (not hex / not a component) so
 * the rendering can evolve without a migration. Colours map to existing theme CSS vars
 * (`purple` → the brand `accent`); icons map to lucide components client-side.
 */
export const SAVED_VIEW_COLORS = ['gray', 'red', 'orange', 'green', 'blue', 'purple', 'pink'] as const;
export const SAVED_VIEW_ICONS = [
    'star',
    'bookmark',
    'flag',
    'user',
    'users',
    'user-check',
    'shield',
    'inbox',
    'filter',
    'clock',
    'calendar',
    'circle-alert',
    'circle-check',
    'trending-up',
    'tag',
    'folder',
] as const;

export const savedViewColorSchema = z.enum(SAVED_VIEW_COLORS);
export const savedViewIconSchema = z.enum(SAVED_VIEW_ICONS);

export type SavedViewColor = z.infer<typeof savedViewColorSchema>;
export type SavedViewIcon = z.infer<typeof savedViewIconSchema>;

/** A safe, empty payload — used when stored JSON is not even an object. */
export const EMPTY_STORED_SAVED_VIEW_PAYLOAD: StoredSavedViewPayload = {
    schemaVersion: SAVED_VIEW_PAYLOAD_VERSION,
    filters: [],
    orderBy: [],
    columns: { visible: [], order: [] },
    pageSize: DEFAULT_PAGE_SIZE,
    params: {},
};

/**
 * Upcast an older stored payload to the current version before validation.
 * No-op at v1; future versions add steps keyed on `raw.schemaVersion`.
 */
export function migrateSavedViewPayload(raw: unknown): unknown {
    return raw;
}
